import { CurrencyPipe } from '@angular/common';
import { Directive, ElementRef, EventEmitter, forwardRef, HostListener, Input, Output, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isNullOrUndefined } from '../../utils/util';

@Directive({
    selector: '[uzCurrencyInput]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CurrencyInputDirective),
            multi: true
        }
    ],
    standalone: true
})
export class CurrencyInputDirective implements ControlValueAccessor {
  @Input() ciDecimals = 2;
  @Input() ciCurrencyCode = 'USD';
  @Input() ciMaximumAmount = 10000;
  @Input() ciAllowNull = false;

  @Output() ciExceededMax: EventEmitter<any> = new EventEmitter();

  private _currencyString: string = '';

  constructor(private _renderer: Renderer2, private _elementRef: ElementRef, private _currencyPipe: CurrencyPipe) {}

  @HostListener('blur', []) onBlur() {
    this.onTouchedCallback();
  }

  @HostListener('focus', []) onFocus() {
    this.onTouchedCallback();
  }

  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case 'Tab':
        // Allow default
        break;
      case 'Backspace':
        this.handleBackspaceKeyPress(e);
        break;
      default:
        if (isNaN(+e.key)) {
          if (!(e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
            e.preventDefault();
          }
        } else {
          this.handleNumericKeyPress(e);
        }
        break;
    }
  }

  onChangeCallback = (value: any) => {};
  onTouchedCallback = () => {};

  writeValue(value: any) {
    const currencyString: string = this.convertCurrencyValueToCurrencyString(value);

    if (currencyString !== this._currencyString) {
      const currencyValue: number = this.convertCurrencyStringToCurrencyValue(currencyString);
      if (currencyValue > this.ciMaximumAmount) {
        setTimeout(() => {
          this.ciExceededMax.emit({ amount: currencyValue, maxAmount: this.ciMaximumAmount });
        }, 1);
        return;
      }
      this._currencyString = currencyString;
    }

    this._renderer.setProperty(this._elementRef.nativeElement, 'value', this.buildElementValue());
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', isDisabled);
  }

  private updateControlValue(value: any) {
    const currencyString: string = this.convertCurrencyValueToCurrencyString(value);

    if (currencyString !== this._currencyString) {
      const currencyValue: number = this.convertCurrencyStringToCurrencyValue(currencyString);
      if (currencyValue > this.ciMaximumAmount) {
        setTimeout(() => {
          this.ciExceededMax.emit({ amount: currencyValue, maxAmount: this.ciMaximumAmount });
        }, 1);
        return;
      }
      this._currencyString = currencyString;
    }

    this._renderer.setProperty(this._elementRef.nativeElement, 'value', this.buildElementValue());
    this.onChangeCallback(this.buildControlValue());
  }

  private handleBackspaceKeyPress(e: any) {
    e.preventDefault();
    if (window.getSelection().toString().length > 0) {
      this.updateControlValue(0);
    } else {
      this.updateControlValue(`${e.target.value.slice(0, e.target.selectionStart - 1)}${e.target.value.slice(e.target.selectionStart)}`);
    }
  }

  private handleNumericKeyPress(e: any) {
    e.preventDefault();
    if (window.getSelection().toString().length > 0) {
      this.updateControlValue(e.key);
    } else {
      this.updateControlValue(
        `${e.target.value.slice(0, e.target.selectionStart)}${e.key}${e.target.value.slice(e.target.selectionStart)}`
      );
    }
  }

  private buildElementValue() {
    const currencyDecimals = '1.' + this.ciDecimals + '-' + this.ciDecimals;
    const controlValue: number = this.buildControlValue();

    // Currency format return value
    if (!isNullOrUndefined(controlValue)) {
      return this._currencyPipe.transform(controlValue, this.ciCurrencyCode, 'symbol', currencyDecimals);
    }
    return '';
  }

  private buildControlValue(): number {
    return this.convertCurrencyStringToCurrencyValue(this._currencyString);
  }

  private convertCurrencyValueToCurrencyString(currencyValue: any): string {
    if (isNullOrUndefined(currencyValue)) {
      return '';
    }

    if (typeof currencyValue === 'number') {
      const currencyDecimals = '1.' + this.ciDecimals + '-' + this.ciDecimals;
      currencyValue = this._currencyPipe.transform(currencyValue, this.ciCurrencyCode, 'symbol', currencyDecimals);
    }

    return currencyValue.toString().replace(/[^0-9]/g, '');
  }

  private convertCurrencyStringToCurrencyValue(currencyString: string): number {
    const strippedValue: string = currencyString.replace(/[^0-9]/g, '');

    if (strippedValue.length === 0) {
      return this.ciAllowNull ? null : 0;
    }
    const parsedInt: number = parseInt(strippedValue, 10);
    return parsedInt / Math.pow(10, this.ciDecimals);
  }
}
